/** `default` uses react-select's own chips; `checkbox` uses checkbox options with a one-chip `+N` summary. */
export type SelectVariant = "default" | "checkbox"

export interface SelectBehaviourInput<Placeholder> {
  variant: SelectVariant
  isMulti: boolean
  /** Drives the `N Selected` placeholder of the checkbox variant. */
  valueLength: number
  placeholder: Placeholder
}

export interface SelectBehaviour<Placeholder> {
  isClearable?: boolean
  closeMenuOnSelect?: boolean
  hideSelectedOptions?: boolean
  blurInputOnSelect?: boolean
  placeholder: Placeholder | string
  maxMenuHeight?: number
}

/**
 * Resolves the react-select props that differ per variant. `default` leaves the clear/hide/blur trio
 * `undefined` so react-select keeps deriving them from `isMulti`; only `checkbox` pins them.
 */
export function resolveSelectBehaviour<Placeholder>({
  variant,
  isMulti,
  valueLength,
  placeholder,
}: SelectBehaviourInput<Placeholder>): SelectBehaviour<Placeholder> {
  if (variant === "checkbox") {
    return {
      isClearable: true,
      closeMenuOnSelect: false,
      hideSelectedOptions: false,
      blurInputOnSelect: false,
      placeholder: valueLength > 0 ? `${valueLength} Selected` : placeholder,
    }
  }
  return {
    placeholder,
    maxMenuHeight: isMulti ? 200 : undefined,
  }
}

/** Honours both spellings (`disabled` is this library's, `isDisabled` react-select's); `maxLimit` never locks an item that is already selected. */
export function isItemDisabled(
  item: { disabled?: boolean; isDisabled?: boolean },
  valueLength: number,
  maxLimit?: number,
  isSelected = false,
): boolean {
  if (item.disabled || item.isDisabled) return true
  if (isSelected) return false
  return typeof maxLimit === "number" && valueLength >= maxLimit
}
